import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  Trophy, Star, Users, Calendar, Clock, MapPin, Plus, Trash2, Edit2,
  Newspaper, ChevronDown, ChevronUp, Save, X, Activity, Target, AlertCircle,
  Upload, Image, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const HSFootballManager = () => {
  const { authAxios } = useAuth();
  const [activeSection, setActiveSection] = useState("teams");
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [news, setNews] = useState([]);
  const [recruits, setRecruits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showRecruitModal, setShowRecruitModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  
  const [editingItem, setEditingItem] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [teamsRes, gamesRes, newsRes, recruitsRes] = await Promise.all([
        authAxios.get('/hs-football/teams'),
        authAxios.get('/hs-football/games?limit=50'),
        authAxios.get('/hs-football/news?limit=50'),
        authAxios.get('/hs-football/recruits?limit=100')
      ]);
      setTeams(teamsRes.data);
      setGames(gamesRes.data);
      setNews(newsRes.data);
      setRecruits(recruitsRes.data);
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = [
    { id: 'teams', label: 'Teams', icon: Users, count: teams.length },
    { id: 'games', label: 'Games', icon: Activity, count: games.length },
    { id: 'news', label: 'News', icon: Newspaper, count: news.length },
    { id: 'recruits', label: 'Recruits', icon: Star, count: recruits.length }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-orbitron text-2xl text-white flex items-center gap-3">
            <Trophy className="w-7 h-7 text-orange-400" />
            Palm Beach HS Football Manager
          </h2>
          <p className="font-rajdhani text-gray-400 mt-1">Manage teams, games, news, and recruiting</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded transition-all ${
              activeSection === section.id
                ? 'bg-orange-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <section.icon className="w-4 h-4" />
            <span className="font-rajdhani">{section.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              activeSection === section.id ? 'bg-white/20' : 'bg-white/10'
            }`}>
              {section.count}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white/5 rounded-lg border border-white/10 p-6">
        {activeSection === 'teams' && (
          <TeamsSection 
            teams={teams} 
            onAdd={() => { setEditingItem(null); setShowTeamModal(true); }}
            onEdit={(team) => { setEditingItem(team); setShowTeamModal(true); }}
            onDelete={async (id) => {
              if (confirm("Delete this team?")) {
                try {
                  await authAxios.delete(`/admin/hs-football/teams/${id}`);
                  setTeams(teams.filter(t => t.id !== id));
                  toast.success("Team deleted");
                } catch (e) {
                  toast.error("Failed to delete team");
                }
              }
            }}
            authAxios={authAxios}
            fetchData={fetchAllData}
          />
        )}
        
        {activeSection === 'games' && (
          <GamesSection 
            games={games}
            teams={teams}
            onAdd={() => { setEditingItem(null); setShowGameModal(true); }}
            onUpdateScore={(game) => { setSelectedGame(game); setShowScoreModal(true); }}
            onDelete={async (id) => {
              if (confirm("Delete this game?")) {
                try {
                  await authAxios.delete(`/admin/hs-football/games/${id}`);
                  setGames(games.filter(g => g.id !== id));
                  toast.success("Game deleted");
                } catch (e) {
                  toast.error("Failed to delete game");
                }
              }
            }}
          />
        )}
        
        {activeSection === 'news' && (
          <NewsSection 
            news={news}
            teams={teams}
            onAdd={() => { setEditingItem(null); setShowNewsModal(true); }}
            onEdit={(item) => { setEditingItem(item); setShowNewsModal(true); }}
            onDelete={async (id) => {
              if (confirm("Delete this news item?")) {
                try {
                  await authAxios.delete(`/admin/hs-football/news/${id}`);
                  setNews(news.filter(n => n.id !== id));
                  toast.success("News deleted");
                } catch (e) {
                  toast.error("Failed to delete news");
                }
              }
            }}
          />
        )}
        
        {activeSection === 'recruits' && (
          <RecruitsSection 
            recruits={recruits}
            onAdd={() => { setEditingItem(null); setShowRecruitModal(true); }}
            onEdit={(recruit) => { setEditingItem(recruit); setShowRecruitModal(true); }}
            onDelete={async (id) => {
              if (confirm("Delete this recruit?")) {
                try {
                  await authAxios.delete(`/admin/hs-football/recruits/${id}`);
                  setRecruits(recruits.filter(r => r.id !== id));
                  toast.success("Recruit deleted");
                } catch (e) {
                  toast.error("Failed to delete recruit");
                }
              }
            }}
          />
        )}
      </div>

      {/* Modals */}
      <TeamModal 
        isOpen={showTeamModal}
        onClose={() => { setShowTeamModal(false); setEditingItem(null); }}
        editingTeam={editingItem}
        authAxios={authAxios}
        onSuccess={() => { fetchAllData(); setShowTeamModal(false); setEditingItem(null); }}
      />
      
      <GameModal 
        isOpen={showGameModal}
        onClose={() => { setShowGameModal(false); setEditingItem(null); }}
        teams={teams}
        authAxios={authAxios}
        onSuccess={() => { fetchAllData(); setShowGameModal(false); }}
      />
      
      <ScoreUpdateModal 
        isOpen={showScoreModal}
        onClose={() => { setShowScoreModal(false); setSelectedGame(null); }}
        game={selectedGame}
        authAxios={authAxios}
        onSuccess={() => { fetchAllData(); setShowScoreModal(false); setSelectedGame(null); }}
      />
      
      <NewsModal 
        isOpen={showNewsModal}
        onClose={() => { setShowNewsModal(false); setEditingItem(null); }}
        editingNews={editingItem}
        teams={teams}
        authAxios={authAxios}
        onSuccess={() => { fetchAllData(); setShowNewsModal(false); setEditingItem(null); }}
      />
      
      <RecruitModal 
        isOpen={showRecruitModal}
        onClose={() => { setShowRecruitModal(false); setEditingItem(null); }}
        editingRecruit={editingItem}
        authAxios={authAxios}
        onSuccess={() => { fetchAllData(); setShowRecruitModal(false); setEditingItem(null); }}
      />
    </div>
  );
};

// Teams Section Component
const TeamsSection = ({ teams, onAdd, onEdit, onDelete, authAxios, fetchData }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="font-orbitron text-lg text-white">Manage Teams</h3>
      <Button onClick={onAdd} className="bg-orange-500 hover:bg-orange-600">
        <Plus className="w-4 h-4 mr-2" /> Add Team
      </Button>
    </div>
    
    {teams.length === 0 ? (
      <div className="text-center py-8 text-gray-500">
        <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No teams added yet</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs font-orbitron text-gray-500 border-b border-white/10">
              <th className="pb-3 pr-4">Team</th>
              <th className="pb-3 pr-4">District</th>
              <th className="pb-3 pr-4">Conference</th>
              <th className="pb-3 pr-4 text-center">Record</th>
              <th className="pb-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {teams.map(team => (
              <tr key={team.id} className="border-b border-white/5">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold"
                      style={{ backgroundColor: team.primary_color + '30', color: team.primary_color }}
                    >
                      {team.short_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-rajdhani text-white">{team.name}</p>
                      <p className="font-rajdhani text-xs text-gray-500">{team.mascot}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4 font-rajdhani text-gray-400">{team.district}</td>
                <td className="py-3 pr-4 font-rajdhani text-gray-400">{team.conference}</td>
                <td className="py-3 pr-4 text-center font-mono text-white">{team.wins}-{team.losses}</td>
                <td className="py-3 text-right">
                  <button onClick={() => onEdit(team)} className="p-2 text-gray-400 hover:text-neon-blue">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(team.id)} className="p-2 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// Games Section Component
const GamesSection = ({ games, teams, onAdd, onUpdateScore, onDelete }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="font-orbitron text-lg text-white">Manage Games</h3>
      <Button onClick={onAdd} className="bg-orange-500 hover:bg-orange-600" disabled={teams.length < 2}>
        <Plus className="w-4 h-4 mr-2" /> Add Game
      </Button>
    </div>
    
    {teams.length < 2 && (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 mb-4">
        <p className="text-yellow-400 text-sm">Add at least 2 teams before scheduling games</p>
      </div>
    )}
    
    {games.length === 0 ? (
      <div className="text-center py-8 text-gray-500">
        <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No games scheduled</p>
      </div>
    ) : (
      <div className="space-y-3">
        {games.map(game => (
          <div key={game.id} className={`bg-black/30 rounded p-4 border ${
            game.status === 'live' ? 'border-red-500' : 'border-white/10'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <span className={`text-[10px] font-orbitron px-2 py-1 rounded ${
                    game.status === 'live' ? 'bg-red-500 text-white' :
                    game.status === 'final' ? 'bg-gray-600 text-white' :
                    'bg-neon-blue/20 text-neon-blue'
                  }`}>
                    {game.status.toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-500">{game.game_date} @ {game.game_time}</span>
                  {game.is_conference_game && (
                    <span className="text-[9px] bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">CONF</span>
                  )}
                </div>
                <div className="flex items-center gap-8">
                  <div>
                    <span className="font-rajdhani text-white">{game.away_team_name}</span>
                    <span className="font-orbitron text-2xl text-white ml-4">{game.away_score}</span>
                  </div>
                  <span className="text-gray-500">@</span>
                  <div>
                    <span className="font-rajdhani text-white">{game.home_team_name}</span>
                    <span className="font-orbitron text-2xl text-white ml-4">{game.home_score}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => onUpdateScore(game)} 
                  variant="outline" 
                  size="sm"
                  className="border-neon-blue text-neon-blue hover:bg-neon-blue/20"
                >
                  <Activity className="w-4 h-4 mr-1" /> Update
                </Button>
                <button onClick={() => onDelete(game.id)} className="p-2 text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// News Section Component  
const NewsSection = ({ news, teams, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="font-orbitron text-lg text-white">Manage News</h3>
      <Button onClick={onAdd} className="bg-orange-500 hover:bg-orange-600">
        <Plus className="w-4 h-4 mr-2" /> Add News
      </Button>
    </div>
    
    {news.length === 0 ? (
      <div className="text-center py-8 text-gray-500">
        <Newspaper className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No news items</p>
      </div>
    ) : (
      <div className="space-y-3">
        {news.map(item => (
          <div key={item.id} className="bg-black/30 rounded p-4 border border-white/10">
            <div className="flex justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-orbitron px-2 py-0.5 rounded ${
                    item.is_breaking ? 'bg-red-500 text-white' : 'bg-white/10 text-gray-400'
                  }`}>
                    {item.is_breaking ? 'BREAKING' : item.category.toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
                <h4 className="font-rajdhani text-white">{item.headline}</h4>
                <p className="font-rajdhani text-sm text-gray-400 line-clamp-1">{item.content}</p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button onClick={() => onEdit(item)} className="p-2 text-gray-400 hover:text-neon-blue">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(item.id)} className="p-2 text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Recruits Section Component
const RecruitsSection = ({ recruits, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="font-orbitron text-lg text-white">Manage Recruits</h3>
      <Button onClick={onAdd} className="bg-orange-500 hover:bg-orange-600">
        <Plus className="w-4 h-4 mr-2" /> Add Recruit
      </Button>
    </div>
    
    {recruits.length === 0 ? (
      <div className="text-center py-8 text-gray-500">
        <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No recruits added</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {recruits.map(recruit => (
          <div key={recruit.id} className="bg-black/30 rounded p-4 border border-white/10">
            <div className="flex gap-3 mb-2">
              {/* Recruit Photo */}
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                {recruit.image_url ? (
                  <img 
                    src={recruit.image_url.startsWith('/') ? `${API}${recruit.image_url}` : recruit.image_url} 
                    alt={recruit.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-gray-600" />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-orbitron text-white text-sm">{recruit.name}</h4>
                    <p className="font-rajdhani text-neon-blue text-xs">{recruit.position} • {recruit.school}</p>
                  </div>
                  <div className="flex">
                    {[...Array(recruit.star_rating)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                  <span>{recruit.height} • {recruit.weight}lbs</span>
                  <span>Class of {recruit.graduation_year}</span>
                </div>
              </div>
            </div>
            
            {recruit.committed_to && (
              <p className="text-xs text-green-400 mb-2">Committed: {recruit.committed_to}</p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => onEdit(recruit)} className="p-2 text-gray-400 hover:text-neon-blue">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => onDelete(recruit.id)} className="p-2 text-gray-400 hover:text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Team Modal
const TeamModal = ({ isOpen, onClose, editingTeam, authAxios, onSuccess }) => {
  const [form, setForm] = useState({
    name: '', short_name: '', mascot: '', district: '', conference: '',
    primary_color: '#00F0FF', secondary_color: '#FF0099', logo_url: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingTeam) {
      setForm(editingTeam);
    } else {
      setForm({ name: '', short_name: '', mascot: '', district: '', conference: '', 
                primary_color: '#00F0FF', secondary_color: '#FF0099', logo_url: '' });
    }
  }, [editingTeam, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTeam) {
        await authAxios.put(`/admin/hs-football/teams/${editingTeam.id}`, form);
        toast.success("Team updated");
      } else {
        await authAxios.post('/admin/hs-football/teams', form);
        toast.success("Team created");
      }
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save team");
    }
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-white">
            {editingTeam ? 'Edit Team' : 'Add New Team'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Full Name *</label>
              <Input 
                value={form.name} 
                onChange={e => setForm({...form, name: e.target.value})}
                placeholder="Palm Beach Central Broncos"
                required
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Short Name *</label>
              <Input 
                value={form.short_name} 
                onChange={e => setForm({...form, short_name: e.target.value})}
                placeholder="PB Central"
                required
                className="bg-black/50 border-white/20"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Mascot *</label>
              <Input 
                value={form.mascot} 
                onChange={e => setForm({...form, mascot: e.target.value})}
                placeholder="Broncos"
                required
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">District *</label>
              <Input 
                value={form.district} 
                onChange={e => setForm({...form, district: e.target.value})}
                placeholder="7A-District 13"
                required
                className="bg-black/50 border-white/20"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Conference *</label>
            <Input 
              value={form.conference} 
              onChange={e => setForm({...form, conference: e.target.value})}
              placeholder="Palm Beach"
              required
              className="bg-black/50 border-white/20"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Primary Color</label>
              <div className="flex gap-2">
                <input 
                  type="color" 
                  value={form.primary_color}
                  onChange={e => setForm({...form, primary_color: e.target.value})}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input 
                  value={form.primary_color} 
                  onChange={e => setForm({...form, primary_color: e.target.value})}
                  className="bg-black/50 border-white/20"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Secondary Color</label>
              <div className="flex gap-2">
                <input 
                  type="color" 
                  value={form.secondary_color}
                  onChange={e => setForm({...form, secondary_color: e.target.value})}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input 
                  value={form.secondary_color} 
                  onChange={e => setForm({...form, secondary_color: e.target.value})}
                  className="bg-black/50 border-white/20"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Logo URL (optional)</label>
            <Input 
              value={form.logo_url} 
              onChange={e => setForm({...form, logo_url: e.target.value})}
              placeholder="https://..."
              className="bg-black/50 border-white/20"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? 'Saving...' : (editingTeam ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Game Modal
const GameModal = ({ isOpen, onClose, teams, authAxios, onSuccess }) => {
  const [form, setForm] = useState({
    home_team_id: '', away_team_id: '', game_date: '', game_time: '',
    venue: '', is_conference_game: false, is_playoff: false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.home_team_id === form.away_team_id) {
      toast.error("Home and away teams must be different");
      return;
    }
    setSaving(true);
    try {
      await authAxios.post('/admin/hs-football/games', form);
      toast.success("Game scheduled");
      onSuccess();
      setForm({ home_team_id: '', away_team_id: '', game_date: '', game_time: '', 
                venue: '', is_conference_game: false, is_playoff: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule game");
    }
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-white">Schedule New Game</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Away Team *</label>
              <Select value={form.away_team_id} onValueChange={v => setForm({...form, away_team_id: v})}>
                <SelectTrigger className="bg-black/50 border-white/20">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.short_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Home Team *</label>
              <Select value={form.home_team_id} onValueChange={v => setForm({...form, home_team_id: v})}>
                <SelectTrigger className="bg-black/50 border-white/20">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.short_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Date *</label>
              <Input 
                type="date"
                value={form.game_date} 
                onChange={e => setForm({...form, game_date: e.target.value})}
                required
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Time *</label>
              <Input 
                value={form.game_time} 
                onChange={e => setForm({...form, game_time: e.target.value})}
                placeholder="7:00 PM"
                required
                className="bg-black/50 border-white/20"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Venue</label>
            <Input 
              value={form.venue} 
              onChange={e => setForm({...form, venue: e.target.value})}
              placeholder="Stadium name"
              className="bg-black/50 border-white/20"
            />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={form.is_conference_game}
                onChange={e => setForm({...form, is_conference_game: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm text-gray-300">Conference Game</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={form.is_playoff}
                onChange={e => setForm({...form, is_playoff: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm text-gray-300">Playoff Game</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? 'Scheduling...' : 'Schedule Game'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Score Update Modal
const ScoreUpdateModal = ({ isOpen, onClose, game, authAxios, onSuccess }) => {
  const [form, setForm] = useState({
    home_score: 0, away_score: 0, quarter: '', time_remaining: '', status: 'scheduled'
  });
  const [highlight, setHighlight] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (game) {
      setForm({
        home_score: game.home_score || 0,
        away_score: game.away_score || 0,
        quarter: game.quarter || '',
        time_remaining: game.time_remaining || '',
        status: game.status || 'scheduled'
      });
    }
  }, [game]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAxios.put(`/admin/hs-football/games/${game.id}`, form);
      toast.success("Game updated");
      onSuccess();
    } catch (e) {
      toast.error("Failed to update game");
    }
    setSaving(false);
  };

  const addHighlight = async () => {
    if (!highlight.trim()) return;
    try {
      await authAxios.post(`/admin/hs-football/games/${game.id}/highlight`, null, {
        params: { highlight: highlight }
      });
      toast.success("Highlight added");
      setHighlight('');
    } catch (e) {
      toast.error("Failed to add highlight");
    }
  };

  if (!game) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-white">
            Update Score: {game.away_team_name} @ {game.home_team_name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Status */}
          <div>
            <label className="text-xs text-gray-400 block mb-1">Game Status</label>
            <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
              <SelectTrigger className="bg-black/50 border-white/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="live">🔴 LIVE</SelectItem>
                <SelectItem value="final">Final</SelectItem>
                <SelectItem value="postponed">Postponed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Scores */}
          <div className="grid grid-cols-2 gap-6">
            <div className="text-center">
              <label className="text-xs text-gray-400 block mb-2">{game.away_team_name}</label>
              <Input 
                type="number"
                value={form.away_score} 
                onChange={e => setForm({...form, away_score: parseInt(e.target.value) || 0})}
                className="bg-black/50 border-white/20 text-center text-2xl font-orbitron"
                min="0"
              />
            </div>
            <div className="text-center">
              <label className="text-xs text-gray-400 block mb-2">{game.home_team_name}</label>
              <Input 
                type="number"
                value={form.home_score} 
                onChange={e => setForm({...form, home_score: parseInt(e.target.value) || 0})}
                className="bg-black/50 border-white/20 text-center text-2xl font-orbitron"
                min="0"
              />
            </div>
          </div>

          {/* Quarter/Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Quarter</label>
              <Select value={form.quarter} onValueChange={v => setForm({...form, quarter: v})}>
                <SelectTrigger className="bg-black/50 border-white/20">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1st">1st Quarter</SelectItem>
                  <SelectItem value="2nd">2nd Quarter</SelectItem>
                  <SelectItem value="Halftime">Halftime</SelectItem>
                  <SelectItem value="3rd">3rd Quarter</SelectItem>
                  <SelectItem value="4th">4th Quarter</SelectItem>
                  <SelectItem value="OT">Overtime</SelectItem>
                  <SelectItem value="Final">Final</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Time Remaining</label>
              <Input 
                value={form.time_remaining} 
                onChange={e => setForm({...form, time_remaining: e.target.value})}
                placeholder="12:00"
                className="bg-black/50 border-white/20"
              />
            </div>
          </div>

          {/* Add Highlight */}
          {form.status === 'live' && (
            <div>
              <label className="text-xs text-gray-400 block mb-1">Add Play Highlight</label>
              <div className="flex gap-2">
                <Input 
                  value={highlight} 
                  onChange={e => setHighlight(e.target.value)}
                  placeholder="TD pass from Smith to Johnson, 45 yards!"
                  className="bg-black/50 border-white/20"
                />
                <Button type="button" onClick={addHighlight} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? 'Updating...' : 'Update Game'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// News Modal
const NewsModal = ({ isOpen, onClose, editingNews, teams, authAxios, onSuccess }) => {
  const [form, setForm] = useState({
    headline: '', content: '', category: 'news', team_id: '',
    image_url: '', source: 'GRIND TIME', is_breaking: false, is_featured: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingNews) {
      setForm(editingNews);
    } else {
      setForm({ headline: '', content: '', category: 'news', team_id: '',
                image_url: '', source: 'GRIND TIME', is_breaking: false, is_featured: false });
    }
  }, [editingNews, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingNews) {
        await authAxios.put(`/admin/hs-football/news/${editingNews.id}`, form);
        toast.success("News updated");
      } else {
        await authAxios.post('/admin/hs-football/news', form);
        toast.success("News published");
      }
      onSuccess();
    } catch (e) {
      toast.error("Failed to save news");
    }
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-white">
            {editingNews ? 'Edit News' : 'Add News Item'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Headline *</label>
            <Input 
              value={form.headline} 
              onChange={e => setForm({...form, headline: e.target.value})}
              required
              className="bg-black/50 border-white/20"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Content *</label>
            <Textarea 
              value={form.content} 
              onChange={e => setForm({...form, content: e.target.value})}
              required
              rows={4}
              className="bg-black/50 border-white/20"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Category</label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="bg-black/50 border-white/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="news">News</SelectItem>
                  <SelectItem value="recruiting">Recruiting</SelectItem>
                  <SelectItem value="ranking">Ranking</SelectItem>
                  <SelectItem value="injury">Injury Report</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Related Team</label>
              <Select value={form.team_id || 'none'} onValueChange={v => setForm({...form, team_id: v === 'none' ? null : v})}>
                <SelectTrigger className="bg-black/50 border-white/20">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.short_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Source</label>
              <Input 
                value={form.source} 
                onChange={e => setForm({...form, source: e.target.value})}
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Image URL</label>
              <Input 
                value={form.image_url} 
                onChange={e => setForm({...form, image_url: e.target.value})}
                placeholder="https://..."
                className="bg-black/50 border-white/20"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={form.is_breaking}
                onChange={e => setForm({...form, is_breaking: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm text-red-400">🔴 Breaking News</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={form.is_featured}
                onChange={e => setForm({...form, is_featured: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm text-gray-300">Featured</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? 'Saving...' : (editingNews ? 'Update' : 'Publish')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Recruit Modal
const RecruitModal = ({ isOpen, onClose, editingRecruit, authAxios, onSuccess }) => {
  const [form, setForm] = useState({
    name: '', position: '', school: '', graduation_year: new Date().getFullYear() + 1,
    height: '', weight: 180, star_rating: 3, national_ranking: null, state_ranking: null,
    position_ranking: null, committed_to: '', top_offers: [], forty_time: '',
    gpa: null, highlights_url: '', image_url: '', twitter_handle: '', bio: '', is_featured: false
  });
  const [offersText, setOffersText] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingRecruit) {
      setForm(editingRecruit);
      setOffersText(editingRecruit.top_offers?.join(', ') || '');
    } else {
      setForm({
        name: '', position: '', school: '', graduation_year: new Date().getFullYear() + 1,
        height: '', weight: 180, star_rating: 3, national_ranking: null, state_ranking: null,
        position_ranking: null, committed_to: '', top_offers: [], forty_time: '',
        gpa: null, highlights_url: '', image_url: '', twitter_handle: '', bio: '', is_featured: false
      });
      setOffersText('');
    }
  }, [editingRecruit, isOpen]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'recruits');

      const response = await authAxios.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setForm({ ...form, image_url: response.data.url });
      toast.success("Photo uploaded!");
    } catch (err) {
      toast.error("Failed to upload photo");
      console.error(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const submitData = {
      ...form,
      top_offers: offersText.split(',').map(o => o.trim()).filter(o => o),
      national_ranking: form.national_ranking || null,
      state_ranking: form.state_ranking || null,
      position_ranking: form.position_ranking || null,
      gpa: form.gpa || null
    };
    
    try {
      if (editingRecruit) {
        await authAxios.put(`/admin/hs-football/recruits/${editingRecruit.id}`, submitData);
        toast.success("Recruit updated");
      } else {
        await authAxios.post('/admin/hs-football/recruits', submitData);
        toast.success("Recruit added");
      }
      onSuccess();
    } catch (e) {
      toast.error("Failed to save recruit");
    }
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-white">
            {editingRecruit ? 'Edit Recruit' : 'Add New Recruit'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Photo Upload Section */}
          <div className="flex items-start gap-4 p-4 bg-black/30 rounded-lg border border-white/10">
            <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
              {form.image_url ? (
                <img 
                  src={form.image_url.startsWith('/') ? `${API}${form.image_url}` : form.image_url} 
                  alt="Recruit"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-gray-600" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-400 block mb-2">Recruit Photo</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                id="recruit-photo-upload"
              />
              <div className="flex gap-2">
                <label
                  htmlFor="recruit-photo-upload"
                  className={`flex items-center gap-2 px-4 py-2 bg-neon-blue/20 hover:bg-neon-blue/30 border border-neon-blue/50 rounded cursor-pointer transition-all ${uploading ? 'opacity-50' : ''}`}
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-neon-blue" />
                  ) : (
                    <Upload className="w-4 h-4 text-neon-blue" />
                  )}
                  <span className="text-sm text-neon-blue">{uploading ? 'Uploading...' : 'Upload Photo'}</span>
                </label>
                {form.image_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: '' })}
                    className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded text-red-400 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">PNG, JPG up to 5MB</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Name *</label>
              <Input 
                value={form.name} 
                onChange={e => setForm({...form, name: e.target.value})}
                required
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Position *</label>
              <Select value={form.position} onValueChange={v => setForm({...form, position: v})}>
                <SelectTrigger className="bg-black/50 border-white/20">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="QB">QB - Quarterback</SelectItem>
                  <SelectItem value="RB">RB - Running Back</SelectItem>
                  <SelectItem value="WR">WR - Wide Receiver</SelectItem>
                  <SelectItem value="TE">TE - Tight End</SelectItem>
                  <SelectItem value="OL">OL - Offensive Line</SelectItem>
                  <SelectItem value="DL">DL - Defensive Line</SelectItem>
                  <SelectItem value="LB">LB - Linebacker</SelectItem>
                  <SelectItem value="CB">CB - Cornerback</SelectItem>
                  <SelectItem value="S">S - Safety</SelectItem>
                  <SelectItem value="K">K - Kicker</SelectItem>
                  <SelectItem value="P">P - Punter</SelectItem>
                  <SelectItem value="ATH">ATH - Athlete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">High School *</label>
              <Input 
                value={form.school} 
                onChange={e => setForm({...form, school: e.target.value})}
                required
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Class Year *</label>
              <Select value={String(form.graduation_year)} onValueChange={v => setForm({...form, graduation_year: parseInt(v)})}>
                <SelectTrigger className="bg-black/50 border-white/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2025, 2026, 2027, 2028, 2029].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Height *</label>
              <Input 
                value={form.height} 
                onChange={e => setForm({...form, height: e.target.value})}
                placeholder="6'2"
                required
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Weight (lbs) *</label>
              <Input 
                type="number"
                value={form.weight} 
                onChange={e => setForm({...form, weight: parseInt(e.target.value) || 0})}
                required
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">40-Yard Time</label>
              <Input 
                value={form.forty_time} 
                onChange={e => setForm({...form, forty_time: e.target.value})}
                placeholder="4.52"
                className="bg-black/50 border-white/20"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 block mb-1">Star Rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setForm({...form, star_rating: star})}
                  className="p-1"
                >
                  <Star className={`w-8 h-8 ${star <= form.star_rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">State Rank</label>
              <Input 
                type="number"
                value={form.state_ranking || ''} 
                onChange={e => setForm({...form, state_ranking: parseInt(e.target.value) || null})}
                placeholder="#"
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">National Rank</label>
              <Input 
                type="number"
                value={form.national_ranking || ''} 
                onChange={e => setForm({...form, national_ranking: parseInt(e.target.value) || null})}
                placeholder="#"
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Position Rank</label>
              <Input 
                type="number"
                value={form.position_ranking || ''} 
                onChange={e => setForm({...form, position_ranking: parseInt(e.target.value) || null})}
                placeholder="#"
                className="bg-black/50 border-white/20"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Committed To</label>
              <Input 
                value={form.committed_to} 
                onChange={e => setForm({...form, committed_to: e.target.value})}
                placeholder="University name"
                className="bg-black/50 border-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">GPA</label>
              <Input 
                type="number"
                step="0.1"
                value={form.gpa || ''} 
                onChange={e => setForm({...form, gpa: parseFloat(e.target.value) || null})}
                placeholder="3.5"
                className="bg-black/50 border-white/20"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 block mb-1">Top Offers (comma separated)</label>
            <Input 
              value={offersText} 
              onChange={e => setOffersText(e.target.value)}
              placeholder="Alabama, Georgia, Florida State"
              className="bg-black/50 border-white/20"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 block mb-1">Twitter Handle</label>
            <Input 
              value={form.twitter_handle} 
              onChange={e => setForm({...form, twitter_handle: e.target.value})}
              placeholder="@handle"
              className="bg-black/50 border-white/20"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 block mb-1">Highlights Video URL</label>
            <Input 
              value={form.highlights_url} 
              onChange={e => setForm({...form, highlights_url: e.target.value})}
              placeholder="https://hudl.com/..."
              className="bg-black/50 border-white/20"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 block mb-1">Bio</label>
            <Textarea 
              value={form.bio} 
              onChange={e => setForm({...form, bio: e.target.value})}
              rows={2}
              className="bg-black/50 border-white/20"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={form.is_featured}
              onChange={e => setForm({...form, is_featured: e.target.checked})}
              className="rounded"
            />
            <span className="text-sm text-yellow-400">⭐ Featured Recruit</span>
          </label>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? 'Saving...' : (editingRecruit ? 'Update' : 'Add Recruit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default HSFootballManager;
